import {COMPANY_KEYS, useCurrentCompanyId} from "@/api/companyApi";
import type {CampaignApplicationChatSerializer, UploadedFileSerializer} from "@/generated/DjangoTypes";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {useFileType} from "@/pages/chat/chatHooks";
import type {TChatSortOption, TPaginated} from "@/store/types";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type Query,
  type UseMutationOptions,
} from "@tanstack/react-query";
import {useParams} from "@tanstack/react-router";
import type {AxiosError, AxiosResponse} from "axios";
import {useState} from "react";
import {useDebounce} from "react-use";

type TBulkMessageInfluencer = {
  uuid: number;
  user: {
    id: number;
    first_name: string;
    last_name: string | null;
  };
  picture: string | null;
  picture320: string | null;
  picture640: string | null;
};

// Shared URL builder for the campaign<->creator thread endpoint. Two distinct
// query keys below point at this same URL - MessagesSection's infinite query
// and the Action Queue drawer's plain query must never share a cache entry,
// since they store incompatible shapes (InfiniteData vs a flat AxiosResponse).
const chatMessageListUrl = ({campaignId, influencerUserId}: {campaignId: number; influencerUserId: number}) =>
  `/chat/paginated_messages/?campaign_id=${campaignId}&recipient_id=${influencerUserId}`;

export const CHAT_KEYS = {
  mChatBulkMessage: ["/chat/bulk-message/"],
  mChatImageUpload: ["/chat/upload_image/"],
  mChatMessage: ["/chat/message/"],
  mChatMessageEdit: (messageId: number) => [`/chat/message/${messageId}/`],
  mChatWsTicket: ["/chat/ws_ticket/"],
  qChatBulkMessageInfluencers: ({campaignId}: {campaignId: number}) => [
    `/campaign/${campaignId}/bulk_message_influencers/`,
  ],
  qChatList: ({companyId}: {companyId: number}) => [`/chat/list/?company_profile_id=${companyId}`],
  qChatMessageList: ({campaignId, influencerUserId}: {campaignId: number; influencerUserId: number}) => [
    chatMessageListUrl({campaignId, influencerUserId}),
  ],
  // Same endpoint as qChatMessageList, but a distinct cache key for the Action
  // Queue chat drawer (see QueueChatDrawer.tsx) - it uses a plain useGenericQuery,
  // while MessagesSection uses useGenericInfiniteQuery against qChatMessageList.
  qChatMessageListDrawer: ({campaignId, influencerUserId}: {campaignId: number; influencerUserId: number}) => [
    chatMessageListUrl({campaignId, influencerUserId}),
    "drawer",
  ],
  qChatsNeedsReply: ({companyId, search}: {companyId: number; search?: string}) => [
    `/chat/needs_reply/?company_profile_id=${companyId}&search=${search ?? ""}`,
  ],
  qChatsOfCampaign: ({campaignId, search, sort}: {campaignId: number; search?: string; sort: TChatSortOption}) => [
    `/chat/company_chat/?campaign_id=${campaignId}&search=${search ?? ""}&sort=${sort}`,
  ],
} as const;

// Backend kill switch: the ticket endpoint returns 503 with this detail when the
// chat socket is turned off centrally. The socket treats it as "stay on REST".
export const CHAT_WS_DISABLED = "chat_ws_disabled";

// Imperative mint (not a query hook) for the WebSocket handshake; rides
// `client`'s access token + 401 refresh flow. Rethrows the kill-switch signal
// as a recognizable Error so the socket can stop instead of reconnect-looping.
export const fetchChatWsTicket = async (): Promise<{expires_in: number; ticket: string}> => {
  try {
    const {data} = await client.post<{expires_in: number; ticket: string}>(CHAT_KEYS.mChatWsTicket[0]);
    return data;
  } catch (error) {
    if ((error as AxiosError<{detail?: string}>)?.response?.data?.detail === CHAT_WS_DISABLED) {
      throw new Error(CHAT_WS_DISABLED);
    }
    throw error;
  }
};

export const CHAT_LIST_URL_PREFIXES = {
  chatsNeedsReply: ({companyId}: {companyId: number}) => `/chat/needs_reply/?company_profile_id=${companyId}&`,
  chatsOfCampaign: ({campaignId}: {campaignId: number}) => `/chat/company_chat/?campaign_id=${campaignId}&`,
} as const;

export const matchChatListQueries = (urlPrefix: string) => ({
  predicate: (query: Query) => (query.queryKey[0] as string)?.startsWith(urlPrefix),
});

export const useInvalidateChatNeedsReply = () => {
  const {campaignId, influencerUserId} = useParams({strict: false});
  const companyId = useCurrentCompanyId();
  const queryClient = useQueryClient();

  return () =>
    [
      {
        queryKey: CHAT_KEYS.qChatMessageList({
          campaignId: Number(campaignId),
          influencerUserId: Number(influencerUserId),
        }),
      },
      matchChatListQueries(CHAT_LIST_URL_PREFIXES.chatsOfCampaign({campaignId: Number(campaignId)})),
      ...(companyId
        ? [
            {queryKey: COMPANY_KEYS.qCompanyNotificationCounts(Number(companyId))},
            matchChatListQueries(CHAT_LIST_URL_PREFIXES.chatsNeedsReply({companyId: Number(companyId)})),
          ]
        : []),
    ].forEach(filters => queryClient.invalidateQueries(filters));
};

const useDebouncedSearch = (search: string) => {
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useDebounce(() => setDebouncedSearch(search), 1000, [search]);

  return debouncedSearch;
};

export const useBulkMessageableInfluencersQuery = ({campaignId, isBulk}: {campaignId: number; isBulk?: boolean}) =>
  useGenericQuery<TBulkMessageInfluencer[]>({
    queryKey: CHAT_KEYS.qChatBulkMessageInfluencers({campaignId}),
    enabled: !!campaignId && isBulk,
  });

export const useChatFileUploadMutation = (
  mutationOptions?: Partial<UseMutationOptions<AxiosResponse<UploadedFileSerializer>, AxiosError, File>>,
) =>
  useMutation<AxiosResponse<UploadedFileSerializer>, AxiosError, File>({
    meta: {skipSuccessToast: true},
    mutationKey: CHAT_KEYS.mChatImageUpload,
    mutationFn: file => {
      const mediaType = useFileType(file.name).type;
      if (mediaType === "unknown") {
        return Promise.reject({
          response: {data: {detail: "unsupported_file_type"}},
        });
      }
      const formData = new FormData();
      if (mediaType === "image") {
        formData.append("image", file);
      } else {
        formData.append("file", file);
      }
      return client.post(CHAT_KEYS.mChatImageUpload[0], formData, {
        headers: {"Content-Type": "multipart/form-data"},
      });
    },
    ...mutationOptions,
  });

export const useChatsOfCampaignQuery = ({
  campaignId,
  search = "",
  sort,
}: {
  campaignId: number;
  search?: string;
  sort: TChatSortOption;
}) => {
  const debouncedSearch = useDebouncedSearch(search);
  const queryKey = CHAT_KEYS.qChatsOfCampaign({campaignId, search: debouncedSearch, sort});

  const query = useInfiniteQuery<
    AxiosResponse<TPaginated<CampaignApplicationChatSerializer> & {bulk_count: number; completed_count: number}>,
    AxiosError,
    {bulkCount: number; completedCount: number; results: CampaignApplicationChatSerializer[]}
  >({
    queryKey,
    initialPageParam: queryKey[0],
    getNextPageParam: lastPage => lastPage.data.next,
    enabled: !!campaignId,
    select: response => ({
      bulkCount: response.pages[0].data.bulk_count,
      completedCount: response.pages[0].data.completed_count,
      results: response.pages.flatMap(page => page.data.results),
    }),
  });

  return {debouncedSearch, query};
};

export const useChatsNeedsReplyQuery = ({
  companyId,
  isEnabled,
  search = "",
}: {
  companyId: number;
  isEnabled: boolean;
  search?: string;
}) => {
  const debouncedSearch = useDebouncedSearch(search);
  const query = useGenericInfiniteQuery<CampaignApplicationChatSerializer>({
    queryKey: CHAT_KEYS.qChatsNeedsReply({companyId, search: debouncedSearch}),
    enabled: isEnabled && !!companyId,
  });

  return {debouncedSearch, query};
};
